import { apiSlice } from "@/redux/rtk/features/apiSlice/apiSlice";
import { toastHandler } from "@/utils/functions";
import queryGenerator from "@/utils/queryGenarator";

export const dealApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getDeal: builder.query({
      query: (id) => ({
        url: `deal/${id}`,
      }),
      providesTags: (result, error, id) => [{ type: "Deal", id }],
    }),
    getDeals: builder.query({
      query: (arg) => {
        const query = queryGenerator(arg);
        return {
          url: `deal?query=all&${query}`,
        };
      },
      providesTags: ["DealAll"],
    }),
    getDealsPaginated: builder.query({
      query: (arg) => {
        const query = queryGenerator(arg);
        return {
          url: `deal?${query}`,
        };
      },
      providesTags: ["Deals"],
    }),
    getDealsConverted: builder.query({
      query: (arg) => {
        const query = queryGenerator(arg);
        return {
          url: `deal?query=converted&${query}`,
        };
      },
      providesTags: ["Deals"],
    }),

    addDeal: builder.mutation({
      query: (values) => ({
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json;charset=UTF-8",
        },
        url: `deal`,
        body: values,
      }),
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          await queryFulfilled;
          toastHandler("Deal added successfully", "success");
        } catch (err) {
          const errorMessage =
            err?.error?.data?.error ||
            err?.error?.data?.message ||
            err?.error?.data?.description ||
            "Something went wrong, Please try again";
          toastHandler(errorMessage, "warning");
        }
      },
      invalidatesTags: ["Deals", "DealAll", "Deal"],
    }),

    deleteDeal: builder.mutation({
      query: ({ id }) => ({
        url: `deal/${id}`,
        method: "DELETE",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json;charset=UTF-8",
        },
      }),
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          await queryFulfilled;
          toastHandler("Deal deleted successfully", "warning");
        } catch (err) {
          const errorMessage =
            err?.error?.data?.error ||
            err?.error?.data?.message ||
            err?.error?.data?.description ||
            "Something went wrong, Please try again";
          toastHandler(errorMessage, "warning");
        }
      },
      invalidatesTags: (result, error, arg) => [
        { type: "Deal", id: arg.id },
        "Deals",
        "DealAll",
      ],
    }),

    updateDeal: builder.mutation({
      query: ({ id, values }) => ({
        url: `deal/${id}`,
        method: "PUT",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json;charset=UTF-8",
        },
        body: values,
      }),
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          await queryFulfilled;
          toastHandler("Deal updated successfully", "success");
        } catch (err) {
          const errorMessage =
            err?.error?.data?.error ||
            err?.error?.data?.message ||
            err?.error?.data?.description ||
            "Something went wrong, Please try again";
          toastHandler(errorMessage, "warning");
        }
      },
      invalidatesTags: (result, error, arg) => [
        { type: "Deal", id: arg.id },
        "Deals",
        "DealAll",
      ],
    }),
    bulkUpdateCommission: builder.mutation({
      query: (values) => ({
        url: `deal/bulk-commission-update`,
        method: "POST",
        body: values,
      }),
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
          toastHandler("Bulk update successful", "success");
        } catch (err) {
          toastHandler("Update failed", "error");
        }
      },
      invalidatesTags: ["Deals", "DealAll"],
    }),
    bulkUpdateDealDetails: builder.mutation({
      query: (values) => ({
        url: `deal/bulk-update-details`,
        method: "POST",
        body: values,
      }),
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
          toastHandler("Bulk update successful", "success");
        } catch (err) {
          toastHandler("Update failed", "error");
        }
      },
      invalidatesTags: ["Deals", "DealAll"],
    }),
    getDealSuggestions: builder.query({
      query: ({ field, key }) => ({
        url: `deal?query=suggestions&field=${field}&key=${key}`,
      }),
    }),
  }),
});

export const {
  useGetDealQuery,
  useGetDealsQuery,
  useGetDealsPaginatedQuery,
  useGetDealsConvertedQuery,
  useAddDealMutation,
  useDeleteDealMutation,
  useUpdateDealMutation,
  useBulkUpdateCommissionMutation,
  useBulkUpdateDealDetailsMutation,
  useGetDealSuggestionsQuery,
} = dealApi;

